// ─── El día del patio, del lado del cliente ───────────────────────────────────
//
// Todo lo que en estas pantallas dice "hoy" tiene que significar el día del
// PATIO, no el del navegador. El backend interpreta un "YYYY-MM-DD" como hora
// de pared del patio (yardDateRange.js, la corrección de #472); si el cliente
// arma o recorta ese mismo día con `new Date("...T00:00:00")` —medianoche del
// navegador— las dos definiciones discrepan y nadie lo dice en pantalla:
//
//  · Al CONSTRUIR el default: un admin en otra zona abre la pantalla, se le
//    manda el día equivocado y ve un día vacío sin ninguna indicación de por
//    qué.
//  · Al RECORTAR en cliente: se descarta en silencio la cola del día que el
//    servidor sí devolvió. Es #472 otra vez, de este lado del cable.
//
// Por eso las dos operaciones viven acá y en el mismo lugar, y por eso el
// módulo está en utils y no dentro de una pantalla: lo usan el historial de
// inspecciones y la pantalla de accesos, y duplicar esta aritmética es
// exactamente lo que produjo la familia de bugs de #472.

/**
 * Fallback cuando no hay patio elegido (la lista abarca todos los del cliente)
 * o el patio no declara zona.
 *
 * OJO: es un literal, y el del backend NO lo es —
 * `process.env.NOTIFICATION_TIMEZONE || "America/Mexico_City"` en
 * yardDateRange.js. Hoy coinciden porque esa variable no está puesta en ningún
 * `.env` ni en ningún workflow, pero ponerla en producción desincroniza los dos
 * recortes EN SILENCIO: el backend devolvería el día de una zona y el cliente
 * descartaría por el de otra, que es exactamente la clase de bug que este
 * módulo documenta. Si algún día se define `NOTIFICATION_TIMEZONE`, este valor
 * tiene que viajar al front (config del build o un campo de la API), no
 * duplicarse a mano acá.
 */
export const DEFAULT_YARD_TIME_ZONE = "America/Mexico_City";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

// Zonas que `Intl` rechaza, memoizadas.
//
// El caso que lo motivó: el catálogo de zonas de la app (`TIMEZONE_OPTIONS`, el
// <select> del formulario de patios) OFRECIÓ 'America/El_Paso' desde el
// 30/4/2026 (b8481468) hasta que el PR #539 la sacó, cuatro meses después, sin
// que nada la detectara. No es IANA — `new Intl.DateTimeFormat` con ella tira
// `RangeError: Invalid time zone specified`. Como `yardDateBounds` se evalúa
// dentro de un `useMemo` en tiempo de render, esa excepción dejaba la pantalla
// de accesos en blanco con sólo elegir ese patio en el filtro.
//
// O sea que hoy grepear `TIMEZONE_OPTIONS` no la encuentra. ESO NO VUELVE
// MUERTO A ESTE GUARD, y borrarlo por no encontrarla sería el error: el
// catálogo sólo gobierna lo que se puede ELEGIR de ahora en más, mientras que
// lo que llega acá es lo que quedó GUARDADO —
// `truck_yards.timeZone`, que nadie valida al escribir (truckYardController.js
// la persiste cruda) y que viaja al front por `TruckYardOption.timeZone`. Un
// documento escrito mientras la opción existía sobrevive intacto a que la
// opción deje de existir.
//
// Hoy no hay ninguno: medido con el script read-only
// `_diag_517_yard_timezones.js` contra las dos bases, y otra vez en la review
// de #539 — producción
// (dumax-lite-production) 7 patios (6 America/Mexico_City, 1 America/Monterrey)
// y 1154 geocercas, CERO con zona inválida. O sea que el guard no está tapando
// datos sucios existentes: cubre el caso de que vuelva a entrar uno, por la
// misma puerta sin validar que dejó pasar 'America/El_Paso' cuatro meses.
//
// El código que esto reemplazó (`new Date("...T00:00:00")`) no podía tirar, así
// que el modo de falla lo introdujo este módulo. Se degrada al fallback, igual
// que `getYardZonedParts` en accessSection: un recorte corrido unas horas es
// malo, una pantalla en blanco es peor.
const invalidZones = new Set<string>();

function safeFormatter(timeZone: string): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = {
    // `hourCycle: "h23"` explícito, no sólo `hour12: false`. Con h24 —que
    // algunos motores eligen para `hour12: false`— la medianoche se reporta
    // como día D hora "24", que significa 00:00 del día D+1: el `day` NO
    // avanza. Normalizar ese "24" a 0 sin tocar el día deja el offset corrido
    // 24 h enteras y `yardDayStartMs` devolvería un día completo equivocado.
    // Node 24 y los navegadores actuales resuelven a h23, así que era latente y
    // no vivo; se fija igual, como ya hace `dateToTimestamp` en el backend.
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  if (!invalidZones.has(timeZone)) {
    try {
      return new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    } catch {
      invalidZones.add(timeZone);
      console.warn(
        `[yardDay] zona horaria inválida "${timeZone}"; se usa ${DEFAULT_YARD_TIME_ZONE}`,
      );
    }
  }
  try {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: DEFAULT_YARD_TIME_ZONE,
    });
  } catch {
    // Un runtime sin la base de datos de zonas. UTC siempre existe.
    return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" });
  }
}

const zoneParts = (utcMs: number, timeZone: string) => {
  const parts = safeFormatter(timeZone).formatToParts(new Date(utcMs));
  const at = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return {
    year: at("year"),
    month: at("month"),
    day: at("day"),
    // Con `hourCycle: "h23"` la medianoche ya viene como "00" y este módulo
    // nunca ve un "24". El `% 24` queda como red: si un motor lo devolviera
    // igual, mapearlo a 0 sin avanzar el día corre el offset 24 h —ver la nota
    // de `hourCycle` arriba—, pero es preferible a un `Date.UTC` con hora 24
    // que silenciosamente desborda al día siguiente sin dejar rastro.
    hour: at("hour") % 24,
    minute: at("minute"),
    second: at("second"),
  };
};

/**
 * Cuánto se corre la zona respecto de UTC en ese instante, en ms.
 * Negativo al oeste de Greenwich; para México da −6 h.
 */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const p = zoneParts(utcMs, timeZone);
  return (
    Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - utcMs
  );
}

/** Medianoche de `dateOnly` ("YYYY-MM-DD") en la zona del patio, en epoch ms. */
export function yardDayStartMs(
  dateOnly: string,
  timeZone: string = DEFAULT_YARD_TIME_ZONE,
): number | null {
  if (!DATE_ONLY_RE.test(dateOnly)) return null;
  const zone = timeZone || DEFAULT_YARD_TIME_ZONE;
  const naiveUtc = Date.parse(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(naiveUtc)) return null;

  // Dos candidatos: el que sale del offset vigente en la medianoche UTC, y el
  // que sale de reevaluar el offset ya parado en ese candidato. En una zona sin
  // cambio de horario coinciden.
  const first = naiveUtc - zoneOffsetMs(naiveUtc, zone);
  const second = naiveUtc - zoneOffsetMs(first, zone);

  // Un candidato es válido si la hora de pared EN ese instante es de verdad la
  // medianoche pedida. Iterar dos veces sin verificar era el bug: en las zonas
  // cuyo salto de horario cae en la medianoche local —America/Santiago el
  // 6/9/2026, donde el reloj va de 00:00 a 01:00— la segunda pasada devolvía
  // las 23:00 del día ANTERIOR. México, Nueva York, Madrid y Kolkata daban
  // bien, incluidos sus días de 23 h y 25 h, porque su salto no es a
  // medianoche: por eso el bug no aparecía en los casos obvios.
  const roundTrips = (candidate: number) =>
    zoneOffsetMs(candidate, zone) === naiveUtc - candidate;

  const okFirst = roundTrips(first);
  const okSecond = roundTrips(second);

  // Medianoche AMBIGUA (el reloj retrocede y la medianoche ocurre dos veces):
  // el día arranca en la primera de las dos.
  if (okFirst && okSecond) return Math.min(first, second);
  if (okFirst) return first;
  if (okSecond) return second;

  // Medianoche INEXISTENTE (el reloj salta hacia adelante desde las 00:00): el
  // día arranca en el primer instante que sí existe, que es el mayor de los dos
  // candidatos — el que cae del otro lado del salto.
  return Math.max(first, second);
}

/** "2026-09-01" → "2026-09-02" (aritmética de calendario, sin zonas). */
function nextCalendarDay(dateOnly: string): string {
  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
}

/**
 * Último milisegundo de `dateOnly` en la zona del patio (inclusivo).
 *
 * Se calcula desde la medianoche del día SIGUIENTE en vez de sumar 24 h: en una
 * zona con horario de verano el día dura 23 h o 25 h, y sumar 24 h dejaría el
 * límite dentro del día siguiente o una hora antes del final del elegido. Es la
 * misma aritmética que yardDateRange.js del backend, a propósito.
 */
export function yardDayEndMs(
  dateOnly: string,
  timeZone: string = DEFAULT_YARD_TIME_ZONE,
): number | null {
  const start = yardDayStartMs(dateOnly, timeZone);
  if (start == null) return null;
  const nextStart = yardDayStartMs(nextCalendarDay(dateOnly), timeZone);
  if (nextStart == null || nextStart <= start) return null;
  return nextStart - 1;
}

/**
 * Los dos extremos del rango, en la zona del patio. Un extremo vacío queda
 * abierto (null), igual que en el backend.
 */
export function yardDateBounds(
  dateFrom: string,
  dateTo: string,
  timeZone: string = DEFAULT_YARD_TIME_ZONE,
): { from: number | null; to: number | null } {
  const zone = timeZone || DEFAULT_YARD_TIME_ZONE;
  return {
    from: dateFrom ? yardDayStartMs(dateFrom, zone) : null,
    to: dateTo ? yardDayEndMs(dateTo, zone) : null,
  };
}

/** El instante `utcMs` como "YYYY-MM-DD" del calendario del patio. */
function toYardDateInput(utcMs: number, timeZone: string): string {
  const p = zoneParts(utcMs, timeZone);
  const mm = String(p.month).padStart(2, "0");
  const dd = String(p.day).padStart(2, "0");
  return `${p.year}-${mm}-${dd}`;
}

/**
 * El día en curso EN EL PATIO, con el formato que espera <input type="date">.
 *
 * `now` es inyectable para los tests; en producción es el reloj real. No se
 * memoiza a propósito: quien lo llame tiene que poder preguntar de nuevo
 * después de la medianoche.
 */
export function yardTodayDateInput(
  timeZone: string = DEFAULT_YARD_TIME_ZONE,
  now: number = Date.now(),
): string {
  return toYardDateInput(now, timeZone || DEFAULT_YARD_TIME_ZONE);
}

/**
 * El día del patio `days` días antes de hoy. Se usa para ampliar el rango a una
 * ventana ACOTADA cuando un deep link apunta a algo fuera del día en curso.
 *
 * En el borde de un cambio de horario puede devolver un día de más: se resta un
 * múltiplo exacto de 24 h desde la medianoche del patio, y un día de 23 h deja
 * el resultado a las 23:00 del día anterior. Es el lado seguro del error — la
 * ventana queda un día más ancha, nunca más angosta— y no vale complicar la
 * aritmética para un uso cuyo único requisito es "que contenga a esta
 * inspección".
 */
export function yardDateInputDaysAgo(
  days: number,
  timeZone: string = DEFAULT_YARD_TIME_ZONE,
  now: number = Date.now(),
): string {
  const zone = timeZone || DEFAULT_YARD_TIME_ZONE;
  const today = yardTodayDateInput(zone, now);
  const startMs = yardDayStartMs(today, zone);
  if (startMs == null) return today;
  return toYardDateInput(startMs - days * DAY_MS, zone);
}
